function describeNode(node: Node): string {
  if (node instanceof Element) {
    const id = node.id ? `#${node.id}` : '';
    const src = node instanceof HTMLIFrameElement && node.src ? ` src="${node.src}"` : '';
    return `<${node.tagName.toLowerCase()}${id}${src}>`;
  }

  return `[${node.constructor?.name ?? 'Node'}]`;
}

/**
 * Makes a log payload safe for JSON.stringify before it reaches NewRelic.
 * DOM nodes carry circular React fibers (`__reactFiber$...` → stateNode → the node),
 * so they are replaced with a short description; other cycles become '[Circular]'.
 */
export function sanitizeLogArgs(value: unknown, ancestors = new WeakSet<object>()): unknown {
  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (typeof Node !== 'undefined' && value instanceof Node) {
    return describeNode(value);
  }

  if (typeof window !== 'undefined' && value === window) {
    return '[Window]';
  }

  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: value.stack };
  }

  if (ancestors.has(value)) {
    return '[Circular]';
  }

  ancestors.add(value);

  const sanitized = Array.isArray(value)
    ? value.map((item) => sanitizeLogArgs(item, ancestors))
    : Object.fromEntries(Object.entries(value).map(([key, item]) => [key, sanitizeLogArgs(item, ancestors)]));

  ancestors.delete(value);

  return sanitized;
}
